import { useState, useEffect, useCallback } from "react";
import {
  Bot, Play, Pause, Square, RotateCcw, RefreshCw, Activity, Circle,
} from "lucide-react";
import BotActivityChart from "./BotActivityChart";
import BotWalletSwitch from "./BotWalletSwitch";
import api from "../lib/api";
import { toast } from "sonner";
import { timeAgo, clsx } from "../lib/format";
import { Button } from "./ui/button";

const STATUS = {
  running: { label: "RUNNING", cls: "text-pos bg-pos/15 border-pos/30", dot: "bg-pos" },
  paused: { label: "PAUSED", cls: "text-gold bg-gold/15 border-gold/30", dot: "bg-gold" },
  stopped: { label: "STOPPED", cls: "text-fx-s3 bg-white/5 border-white/10", dot: "bg-fx-s3" },
};

const KIND_COLOR = { open: "text-brandblue", win: "text-pos", loss: "text-neg", start: "text-gold", info: "text-fx-s3" };

function fmtDur(s) {
  if (!s) return "0s";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${h ? h + "h " : ""}${m ? m + "m " : ""}${sec}s`;
}

export default function BotInstanceCard({ inst, onChanged }) {
  const [activity, setActivity] = useState([]);
  const st = STATUS[inst.status] || STATUS.stopped;
  const p = inst.performance || {};
  const lid = inst.id;

  const loadActivity = useCallback(async () => {
    try { const { data } = await api.get(`/bots/instances/${lid}/activity`); setActivity(data); } catch {}
  }, [lid]);

  useEffect(() => {
    loadActivity();
    const t = setInterval(loadActivity, 2500);
    return () => clearInterval(t);
  }, [loadActivity]);

  const control = async (action) => {
    try {
      await api.post(`/bots/instances/${lid}/control`, { action });
      toast.success(`Bot ${action}`);
      window.dispatchEvent(new Event("fx:bots-changed"));
      onChanged?.();
      loadActivity();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Action failed");
    }
  };

  return (
    <div data-testid={`bot-instance-${lid}`} className="rounded-xl bg-fx-s1 border border-white/5 overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {inst.bot?.logo_url
              ? <img src={inst.bot.logo_url} alt={inst.bot?.name} className="h-14 w-14 rounded-2xl object-cover border border-white/10 bg-fx-base" />
              : <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-brandblue/25 to-brandblue/5 border border-brandblue/20 grid place-items-center text-brandblue"><Bot className="h-7 w-7" /></div>}
            <div>
              <div className="font-display font-bold">{inst.bot?.name}</div>
              <div className="text-xs text-fx-s3 font-mono">{inst.asset} · ${inst.trade_amount}/trade · {inst.duration}s</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span data-testid={`bot-wallet-badge-${lid}`} className={clsx("px-2 py-0.5 rounded text-[11px] font-mono font-bold border", inst.wallet_mode === "live" ? "text-neg bg-neg/15 border-neg/30" : "text-brandblue bg-brandblue/10 border-brandblue/25")}>{inst.wallet_mode === "live" ? "LIVE" : "DEMO"}</span>
            <span className={clsx("px-2 py-0.5 rounded text-[11px] font-mono font-semibold border flex items-center gap-1.5", st.cls)}>
              <Circle className={clsx("h-2 w-2 rounded-full", st.dot, inst.status === "running" && "pulse-dot")} fill="currentColor" /> {st.label}
            </span>
          </div>
        </div>
        <BotWalletSwitch inst={inst} onChanged={onChanged} />

        {/* live state */}
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-fx-base border border-white/5 px-3 py-2">
          <Activity className={clsx("h-4 w-4", inst.status === "running" ? "text-brandblue pulse-dot" : "text-fx-s3")} />
          <span data-testid={`bot-state-${lid}`} className="text-sm font-mono text-white">{p.activity_state || inst.activity_state || "Idle"}</span>
          {inst.status === "running" && <span className="ml-auto text-xs text-fx-s3 font-mono">up {fmtDur(p.running_time_sec)}</span>}
        </div>

        <BotActivityChart lid={lid} asset={inst.asset} activity={activity} running={inst.status === "running"} />

        {/* controls */}
        <div className="flex flex-wrap gap-2 mt-4">
          {inst.status !== "running" && <Button size="sm" data-testid={`bot-start-${lid}`} onClick={() => control("start")} className="bg-pos hover:bg-emerald-500 gap-1"><Play className="h-3.5 w-3.5" /> Start</Button>}
          {inst.status === "running" && <Button size="sm" data-testid={`bot-pause-${lid}`} onClick={() => control("pause")} className="bg-gold hover:bg-amber-500 text-black gap-1"><Pause className="h-3.5 w-3.5" /> Pause</Button>}
          {inst.status === "paused" && <Button size="sm" data-testid={`bot-resume-${lid}`} onClick={() => control("resume")} className="bg-pos hover:bg-emerald-500 gap-1"><Play className="h-3.5 w-3.5" /> Resume</Button>}
          {inst.status !== "stopped" && <Button size="sm" variant="outline" data-testid={`bot-stop-${lid}`} onClick={() => control("stop")} className="border-white/10 bg-fx-base gap-1"><Square className="h-3.5 w-3.5" /> Stop</Button>}
          <Button size="sm" variant="outline" data-testid={`bot-restart-${lid}`} onClick={() => control("restart")} className="border-white/10 bg-fx-base gap-1"><RefreshCw className="h-3.5 w-3.5" /> Restart</Button>
          <Button size="sm" variant="outline" data-testid={`bot-reset-${lid}`} onClick={() => control("reset")} className="border-white/10 bg-fx-base text-neg gap-1"><RotateCcw className="h-3.5 w-3.5" /> Reset</Button>
        </div>
      </div>

      {/* activity feed */}
      <div className="border-t border-white/5 bg-fx-base/50 max-h-40 overflow-y-auto">
        <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-fx-s3">System Activity</div>
        {activity.length === 0 && <div className="px-4 pb-3 text-xs text-fx-s3">No activity yet.</div>}
        {activity.map((a) => (
          <div key={a.id} className="px-4 py-1.5 flex items-center gap-2 text-xs border-t border-white/[0.02]">
            <span className={clsx("font-mono", KIND_COLOR[a.kind] || "text-fx-s3")}>{a.message}</span>
            <span className="ml-auto text-fx-s3 shrink-0">{timeAgo(a.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
